
import { defineStore } from "pinia";
import { useUsuariosStore } from "./usuarios";
import { usePacientesStore } from "./pacientes";
import { storage } from "@/storage";
import { STORAGE_KEYS } from "@/storage/keys";
import { collection, doc, setDoc, updateDoc, query, where, onSnapshot, writeBatch, arrayUnion } from "firebase/firestore";
import { db } from "@/firebase/firestore";
import { useToast } from "@/composables/useToast";


export const useTurnosStore = defineStore("turnos", {
  state: () => ({
    turnos: [],
    loading: false,
    error: null,
    modoOffline: false,
    _unsubscribeTurnos: null,
    _reintentoTimer: null,
    _reintentoDelay: 5000,
  }),


  actions: {
    iniciarEscucha() {
      const usuariosStore = useUsuariosStore();
      const usuarioId = usuariosStore.usuarioActual?.id || usuariosStore.usuarioActual?.uid;
      if (!usuarioId) return;

      // Desuscribir listener anterior si existe (reintento, cambio de usuario)
      if (this._unsubscribeTurnos) {
        this._unsubscribeTurnos();
        this._unsubscribeTurnos = null;
      }

      const q = query(collection(db, "turnos"), where("usuarioId", "==", usuarioId));

      this._unsubscribeTurnos = onSnapshot(
        q,
        { includeMetadataChanges: true },
        (snapshot) => {
          // Ignorar las emisiones con escrituras locales pendientes (igual que en pacientes).
          // Solo procesamos lo confirmado por el servidor; así no se descartan cambios hechos
          // en otro dispositivo durante una escritura local, ni queda nada "trabado".
          if (snapshot.metadata.hasPendingWrites) return;
          // Ignorar el vacío que viene SOLO de la caché (misma guarda que en pacientes): antes de
          // responder el servidor, Firestore entrega un primer snapshot vacío desde caché. Tomarlo
          // por real vaciaba el estado y pisaba el respaldo de localStorage con una lista vacía.
          if (snapshot.empty && snapshot.metadata.fromCache) return;

          this.turnos = snapshot.docs
            .map((doc) => {
              const numId = Number(doc.id);
              return { id: Number.isNaN(numId) ? doc.id : numId, ...doc.data() };
            })
            // Un turno se borra marcándolo `estado: 'eliminada'` (única bandera, ver
            // cambiarEstadoTurno). Lo excluimos acá, en la carga, así no queda en memoria
            // ni depende de que cada vista se acuerde de filtrarlo.
            .filter((t) => t.usuarioId === usuarioId && t.estado !== "eliminada");

          this.guardarEnLocalStorage();
          this.modoOffline = false;
          this.error = null;
          this.loading = false;
          // Conexión OK: cancelar cualquier reintento pendiente y resetear el backoff.
          this._reintentoDelay = 5000;
          if (this._reintentoTimer) { clearTimeout(this._reintentoTimer); this._reintentoTimer = null; }
        },
        (e) => {
          // Firestore no disponible — caer a localStorage
          console.warn("onSnapshot turnos falló, usando localStorage:", e);
          this.modoOffline = true;
          this.error = "Sin conexión a la nube. Reintentando…";
          const data = storage.get(STORAGE_KEYS.TURNOS);
          if (data && data.length > 0) {
            this.turnos = data.filter((t) => t.usuarioId === usuarioId && t.estado !== "eliminada");
          } else {
            this.turnos = [];
          }
          this.loading = false;
          // Reintento automático con backoff (5s → 15s → 45s → 60s): sin esto, un blip de red
          // dejaba la app en solo-lectura hasta recargar a mano. iniciarEscucha re-suscribe solo.
          if (this._reintentoTimer) clearTimeout(this._reintentoTimer);
          const delay = this._reintentoDelay;
          this._reintentoDelay = Math.min(this._reintentoDelay * 3, 60000);
          this._reintentoTimer = setTimeout(() => {
            this._reintentoTimer = null;
            this.iniciarEscucha();
          }, delay);
        }
      );
    },

    _verificarConexion() {
      // Online-only: bloqueamos escrituras si no hay conexión, para que un dato escrito
      // sin internet NO quede solo en memoria y se pierda al recargar (nunca llegaba a la nube).
      if (this.modoOffline || !navigator.onLine) {
        const { error: toastError } = useToast();
        toastError("Sin conexión: no se guardaron los cambios.");
        return false;
      }
      return true;
    },

    reintentarConexion() {
      this.modoOffline = false;
      this.error = null;
      this.cargarDesdeLocalStorage();
    },

    cargarDesdeLocalStorage() {
      this.loading = true;
      this.error = null;
      const usuariosStore = useUsuariosStore();
      const usuarioId = usuariosStore.usuarioActual?.id || usuariosStore.usuarioActual?.uid;
      if (!usuarioId) {
        this.loading = false;
        return;
      }
      this.iniciarEscucha();
    },

    detenerEscucha() {
      if (this._unsubscribeTurnos) {
        this._unsubscribeTurnos();
        this._unsubscribeTurnos = null;
      }
      if (this._reintentoTimer) { clearTimeout(this._reintentoTimer); this._reintentoTimer = null; }
    },

    guardarEnLocalStorage() {
      const usuariosStore = useUsuariosStore();
      const usuarioId = usuariosStore.usuarioActual?.id || usuariosStore.usuarioActual?.uid;
      // Preservar turnos de otros usuarios en el mismo dispositivo
      const existentes = storage.get(STORAGE_KEYS.TURNOS) || [];
      const deOtrosUsuarios = existentes.filter(
        (t) => t.usuarioId && t.usuarioId !== usuarioId
      );
      storage.set(STORAGE_KEYS.TURNOS, [...deOtrosUsuarios, ...this.turnos]);
    },

    async guardarTurnoEnFirestore(turno, camposAActualizar = null) {
      try {
        const turnoRef = doc(db, "turnos", String(turno.id));
        if (camposAActualizar) {
          await updateDoc(turnoRef, camposAActualizar);
        } else {
          await setDoc(turnoRef, turno, { merge: true });
        }
      } catch (e) {
        console.warn("No se pudo guardar turno en Firestore:", e);
        const { error: toastError } = useToast();
        toastError("No se pudo guardar el turno en la nube. Revisá tu conexión y volvé a intentarlo.");
      }
    },

    // Devuelve true/false para que quien llama pueda avisar el resultado de verdad (antes no
    // devolvía nada y la UI daba "guardado" por sentado).
    agregarTurno(turno) {
      if (!this._verificarConexion()) return false;
      try {
        this.loading = true;
        const usuariosStore = useUsuariosStore();
        const usuarioId = usuariosStore.usuarioActual?.id || usuariosStore.usuarioActual?.uid;
        const turnoConUsuario = { ...turno, usuarioId: usuarioId || null };
        this.turnos.push(turnoConUsuario);
        this.guardarEnLocalStorage();
        this.guardarTurnoEnFirestore(turnoConUsuario);
        return true;
      } catch (e) {
        this.error = "No se pudo agregar el turno.";
        return false;
      } finally {
        this.loading = false;
      }
    },

    // Crea un turno de forma ATÓMICA en sus DOS lugares (colección `turnos` + array del paciente)
    // con un writeBatch: o se guardan los dos, o ninguno. Evita que la Agenda y la ficha del
    // paciente queden desincronizadas (turno en una y no en la otra). Refleja en local solo si la
    // nube confirma. (P1-2)
    async agregarTurnoConFicha(turno, idPaciente) {
      if (!this._verificarConexion()) return false;
      const usuariosStore = useUsuariosStore();
      const usuarioId = usuariosStore.usuarioActual?.id || usuariosStore.usuarioActual?.uid;
      const turnoConUsuario = { ...turno, usuarioId: usuarioId || null };
      try {
        const batch = writeBatch(db);
        batch.set(doc(db, "turnos", String(turno.id)), turnoConUsuario, { merge: true });
        batch.update(doc(db, "pacientes", String(idPaciente)), { turnos: arrayUnion(turno) });
        await batch.commit();
      } catch (e) {
        console.warn("No se pudo agregar el turno (batch atómico):", e);
        const { error: toastError } = useToast();
        toastError("No se pudo guardar el turno en la nube. Revisá tu conexión y volvé a intentarlo.");
        return false;
      }
      // Confirmado por la nube: reflejar en local en los dos lados.
      this.turnos.push(turnoConUsuario);
      this.guardarEnLocalStorage();
      const pacientesStore = usePacientesStore();
      const paciente = pacientesStore.pacientes.find((p) => p.id === idPaciente);
      if (paciente) {
        if (!Array.isArray(paciente.turnos)) paciente.turnos = [];
        paciente.turnos.push(turno);
        pacientesStore.guardarEnLocalStorage();
      }
      return true;
    },


    cambiarEstadoTurno(id, estado) {
      if (!this._verificarConexion()) return;
      try {
        this.loading = true;
        const usuariosStore = useUsuariosStore();
        const usuarioId = usuariosStore.usuarioActual?.id || usuariosStore.usuarioActual?.uid;
        const turno = this.turnos.find((t) => String(t.id) === String(id) && t.usuarioId === usuarioId);
        if (turno) {
          turno.estado = estado;
          this.guardarEnLocalStorage();
          this.guardarTurnoEnFirestore(turno, { estado });
        }
      } catch (e) {
        this.error = "No se pudo cambiar el estado del turno.";
      } finally {
        this.loading = false;
      }
    },


    actualizarTurno(id, datos) {
      if (!this._verificarConexion()) return;
      try {
        this.loading = true;
        const usuariosStore = useUsuariosStore();
        const usuarioId = usuariosStore.usuarioActual?.id || usuariosStore.usuarioActual?.uid;
        const turno = this.turnos.find((t) => String(t.id) === String(id) && t.usuarioId === usuarioId);
        if (turno) {
          Object.assign(turno, datos);
          this.guardarEnLocalStorage();
          this.guardarTurnoEnFirestore(turno, datos);
        }
      } catch (e) {
        this.error = "No se pudo actualizar el turno.";
      } finally {
        this.loading = false;
      }
    },
  },
});